import { EProductTourTargets } from '@constants';
import { IPost } from '@sdk/contracts';
import { Empty } from 'antd';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { WidgetTitle, WidgetWrapper } from '../shared/WidgetShared.styles';

import { PostItem } from './PostItem';
import { PostViewer } from './PostViewer';
import { PostsWrapper, TitleWrapper } from './PostWidget.styles';
import { TopicSelector } from './TopicSelector';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { useStudentPostsList } from '@/store';

const LOADER_SIZE = 40;

export const PostWidget: React.FC = () => {
  const SCROLL_THRESHOLD = 10;

  const { posts, isPending, isFetchingNextPage, hasNextPage, loadMorePosts, topic, setTopic } =
    useStudentPostsList();
  const { t } = useTranslation('StudentDashboardTranslations');
  const listRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(() => {
    if (listRef.current && hasNextPage && !isFetchingNextPage) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      const scrollPos = scrollHeight - scrollTop - clientHeight;
      if (scrollPos < SCROLL_THRESHOLD) loadMorePosts();
    }
  }, [hasNextPage, isFetchingNextPage, loadMorePosts]);

  const shouldBeSetAsFeatured = useCallback((post: IPost, index: number) => {
    const isFirstOrSecond = index === 0 || index === 1;
    return isFirstOrSecond && post.isFeatured;
  }, []);

  const handleTopicChange = (value?: string) => {
    setTopic(value);
  };

  return (
    <WidgetWrapper id={EProductTourTargets.POSTS_WIDGET}>
      <TitleWrapper>
        <WidgetTitle>{t('NEWS')}</WidgetTitle>
        <TopicSelector value={topic} onChange={handleTopicChange} />
      </TitleWrapper>

      <PostsWrapper ref={listRef} onScroll={onScroll}>
        {posts.length === 0 && <Empty description={t('NO_NEWS_TO_SHOW')} />}
        {posts.map((post, index) => (
          <PostItem key={post._id} post={post} isFeatured={shouldBeSetAsFeatured(post, index)} />
        ))}
        {(isPending || isFetchingNextPage) && <LoadingCircleSpinner size={LOADER_SIZE} />}
      </PostsWrapper>
      <PostViewer />
    </WidgetWrapper>
  );
};
